import type { AcpClientOptions } from '../../clients/acp-client.js';
import type { AcpBridgeHooksOptions } from '../acp-bridge-types.js';
import { PathSnapshotTracker } from './path-snapshot-tracker.js';
import { sendSessionInfoTitleUpdate } from './send-session-info-title-update.js';
import { parseBridgeSessionUpdate } from './parse-bridge-session-update.js';
import {
  handleBridgeToolSessionUpdate,
  resolveBridgeToolPaths,
} from './handle-bridge-tool-session-update.js';
import { forwardBridgeSessionUpdate } from './forward-bridge-session-update.js';

export function createBridgeOnSessionUpdate(opts: AcpBridgeHooksOptions): NonNullable<AcpClientOptions['onSessionUpdate']> {
  const { resolveRouting, getSendSessionUpdate, log, sessionParentPath, nextTranscriptStreamSeq } = opts;
  const pathTracker = new PathSnapshotTracker();

  return (params: unknown) => {
    const routing = resolveRouting();
    const runId = routing?.runId;
    const sessionId = routing?.sessionId;
    pathTracker.onRunIdChanged(runId);

    const send = getSendSessionUpdate();
    const sentFileChangePaths = new Set<string>();

    const { updateKind, isToolUpdate, isCompletedToolCallUpdate } = parseBridgeSessionUpdate(params);

    if (updateKind === 'config_option_update') {
      return;
    }
    if (updateKind === 'session_info_update') {
      sendSessionInfoTitleUpdate({ payload: params, runId, sessionId, send, log });
      return;
    }

    const toolPaths = resolveBridgeToolPaths(params, isToolUpdate, sessionParentPath);
    const toolKey = isToolUpdate ? pathTracker.resolveToolKey(params, updateKind) : '';
    const status = (params as { status?: unknown }).status;

    handleBridgeToolSessionUpdate({
      params,
      updateKind,
      isToolUpdate,
      isCompletedToolCallUpdate,
      toolKey,
      toolPaths,
      status,
      sessionParentPath,
      pathTracker,
      sentFileChangePaths,
      send,
      runId: runId ?? '',
      sessionId: sessionId ?? '',
      log,
    });

    if (runId && send) {
      forwardBridgeSessionUpdate({
        runId,
        sessionId,
        updateKind,
        payload: params,
        send,
        nextTranscriptStreamSeq,
        log,
      });
    }
  };
}
